import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CancelSubscription, GetSubscriptionUsers } from "../../../Api/subscription";
import { Confirmation, ErrorToast, SuccessToast } from "../../../Utils/SweetAlert";
import Tooltip from "@mui/material/Tooltip";
import { MdInfo } from "react-icons/md";
import { formatDate } from "../../../Utils/CommanFunctions";


const getRenewalDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
        const d = new Date(dateStr);
        d.setDate(d.getDate() + 1);
        return formatDate(d);
    } catch {
        return "-";
    }
};

const PaidUserTab = ({ filters = {}, page = 0, rowsPerPage = 10, onLoaded }) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const params = useMemo(() => ({
        page: page === 0 ? 1 : page + 1,
        limit: rowsPerPage,
        search: filters?.search || "",
        planType: filters?.planType && filters.planType !== 'all' ? filters.planType : undefined,
        ...(filters?.type ? { type: filters.type } : {}),
        ...(filters?.startDate ? { startDate: filters.startDate } : {}),
        ...(filters?.endDate ? { endDate: filters.endDate } : {}),
        ...(filters?.userId ? { userId: filters.userId } : {}),
    }), [page, rowsPerPage, filters]);

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const response = await GetSubscriptionUsers(params);
            const payload = response?.data || {};
            setItems(payload?.data || []);
            onLoaded && onLoaded({
                total: payload?.totalRecords || 0,
                totals: payload?.totals || null,
            });
        } catch (error) {
            ErrorToast(error?.response?.data?.message || "Server Error.")
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [params]);

    const handleCancelSubscription = async (userId, message = "") => {
        try {
            const result = await Confirmation(message || "Are you sure you want to cancel subscription?");
            if (result.isConfirmed) {
                const response = await CancelSubscription({ userId });
                SuccessToast(response?.message || "Subscription cancelled successfully.");
                fetchUsers();
            }
        } catch (error) {
            ErrorToast(error?.response?.data?.message || "Failed to cancel subscription.");
        }
    };

    return (
        <div className="cards-table card-table">
            <div className="card-body">
                <div className="table-responsive custom-pagination">
                    <table className="table datatable table-manager mb-0 table-no-padding" style={{ width: '100%', tableLayout: 'fixed' }}>
                        <thead className="thead-light">
                            <tr>
                                <th className="text-start" style={{ width: '22%' }}>Name</th>
                                <th className="text-start" style={{ width: '25%' }}>Email</th>
                                <th className="text-start" style={{ width: '10%' }}>Plan Type</th>
                                <th className="text-start" style={{ width: '10%' }}>Method</th>
                                <th className="text-start" style={{ width: '10%' }}>Start</th>
                                <th className="text-start" style={{ width: '10%' }}>Renewal</th>
                                <th className="text-start" style={{ width: '18%' }}>
                                    Action
                                    <Tooltip
                                        title={
                                            <ul className="m-0 p-0 ps-3">
                                                <li><b>Cash payment:</b> Cancelling ends access immediately.</li>
                                                <li><b>Stripe payment:</b> Access continues until the billing period ends.</li>
                                                <li><b>Apple/Google payment:</b> Subscription must be managed through the respective App Store/Play Store settings.</li>
                                            </ul>
                                        }
                                        arrow
                                    >
                                        <span>
                                            <MdInfo className="ms-1 cursor-pointer" style={{ fontSize: '16px', color: '#6c757d' }} />
                                        </span>
                                    </Tooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td align="center" colSpan={7}>
                                        <div className="d-flex justify-content-center align-items-center py-4">
                                            <div className="spinner-border loader-theme" role="status" />
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!isLoading && items?.length === 0 && (
                                <tr>
                                    <td className="text-center" colSpan={7}>No Records Found.</td>
                                </tr>
                            )}
                            {!isLoading && items?.map((row) => (
                                <tr key={row?._id}>
                                    <td className="text-start">
                                        <Tooltip title={row?.name || '-'} placement="top" arrow>
                                            <span className="user-cell-ellipsis">
                                                {row?._id ? (
                                                    <Link className="subscription-neutral-link" to="/admin/client-user-detail" state={{ id: row?._id }}>
                                                        {row?.name || '-'}
                                                    </Link>
                                                ) : (row?.name || '-')}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className="text-start">
                                        <Tooltip title={row?.email || '-'} placement="top" arrow>
                                            <span className="user-cell-ellipsis">
                                                {row?.email || '-'}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className="text-capitalize">{row?.subscriptionPlanType || '-'}</td>
                                    <td className="text-capitalize">
                                        {row?.subscription?.paymentMethod === "cash" ? "cash" :
                                            row?.subscription?.paymentMethod === "stripe" ? "Stripe" :
                                                row?.subscription?.paymentMethod === "apple_pay" ? "Apple" :
                                                    row?.subscription?.paymentMethod === "google_play" ? "Google" :
                                                        "Stripe"}
                                    </td>
                                    <td className="text-start">{formatDate(row?.subscription?.startDate)}</td>
                                    <td className="text-start">{row?.isCancelSubscription ? "-" : getRenewalDate(row?.subscription?.endDate)}</td>
                                    <td className="text-start">
                                        <button
                                            className={`btn ${row?.isCancelSubscription ? 'btn-secondary' : row?.subscription?.paymentMethod === "cash" ? 'btn-danger' : ''}`}
                                            onClick={() => handleCancelSubscription(row?._id, row?.subscription?.paymentMethod === "cash" ? "Are you sure you want to revoke the subscription?" : "Are you sure you want to cancel the subscription?")}
                                            disabled={row?.isCancelSubscription || row?.subscription?.paymentMethod === "apple_pay" || row?.subscription?.paymentMethod === "google_play"}
                                            style={{
                                                minWidth: '170px',
                                                backgroundColor: "#AD5ADC",
                                                color: "#fff",
                                                border: "none"
                                            }}
                                        >
                                            {row?.isCancelSubscription
                                                ? "Cancelled"
                                                : row?.subscription?.paymentMethod === "cash"
                                                    ? "Revoke Subscription"
                                                    : "Cancel Subscription"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PaidUserTab;
